// Reliable file download for the Emergent preview iframe.
//
// The Emergent platform attaches a service worker to the iframe that can
// intercept and mangle responses. Plain anchor clicks (`<a target="_blank">`)
// or hidden-iframe tricks don't reliably trigger Chrome's download manager
// inside that environment.
//
// This implementation fetches the file ourselves with credentials, wraps the
// bytes in a Blob URL on our own origin, then clicks a real `<a download>`
// element. Same-origin blob URLs with a `download` attribute always trigger
// the download manager, regardless of service workers or sandbox attributes.
//
// Usage:
//   downloadFromBackend("/customers/abc/price-report/pdf", { date_from, date_to });
import api from "./api";

export async function downloadFromBackend(path, params = {}) {
  // Strip leading /api if caller included it
  const cleanPath = path.replace(/^\/api/, "");
  // Pull a sensible filename from Content-Disposition, falling back to the URL tail
  const r = await api.get(cleanPath, { params, responseType: "blob" });
  const cd = r.headers["content-disposition"] || "";
  const m = /filename="?([^";]+)"?/i.exec(cd);
  const filename = m ? m[1] : cleanPath.split("/").pop() || "download";
  const blob = new Blob([r.data], { type: r.headers["content-type"] || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 1000);
  return filename;
}

// Build a fully-qualified backend URL with the session token appended as
// `?token=` so it works when opened in a new tab/popup (cookies may not
// cross frame boundaries reliably).
export function backendDownloadUrl(path, params = {}) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const qp = new URLSearchParams(params);
  const tok = typeof window !== "undefined" ? localStorage.getItem("gf_session_token") : "";
  if (tok) qp.set("token", tok);
  const qs = qp.toString();
  return `${base}/api${path}${qs ? `?${qs}` : ""}`;
}

// Backward-compat shim used by older call sites.
export function startDownload(url) {
  // We can no longer rely on iframe / new-tab tricks — just kick off a fetch.
  // Strip the absolute origin so axios' baseURL handles routing.
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/^\/api/, "") + (u.search || "");
    downloadFromBackend(path, {});
  } catch (_) {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}
